/**
 * MoveCard — reusable card component for leads, quotes, and jobs.
 *
 * Three design options are available via the `design` prop:
 *   A: "Compact Row" — tight horizontal layout, icon + key info + status
 *   B: "Structured" — clear rows with labeled fields, more data visible
 *   C: "Badge Strip" — minimal with inline badges for metadata
 *
 * All designs share the same data shape and adapt to the lifecycle stage.
 */

import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ClipboardPen,
  Home,
  MapPin,
  Truck,
  UserPlus,
  Users,
} from 'lucide-react';
import type { Quote } from '../../types/admin';
import { formatMoveDate } from '../../types/admin';

// ── Types ───────────────────────────────────────────────────────────────────

export type LifecycleStage = 'lead' | 'quote' | 'job';

export interface MoveCardProps {
  /** The raw quote/job data — accepts Quote, Job, or any object with similar shape */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  /** Which lifecycle stage to render for */
  stage: LifecycleStage;
  /** Design variant (kept for API compatibility, only 'C' is used) */
  design?: 'C';
  /** Status label */
  statusLabel: string;
  /** Status badge CSS classes */
  statusClasses: string;
  /** Optional click handler (defaults to navigate to /moves/{id}) */
  onClick?: () => void;
  /** Optional reason text (for My Queue context) */
  reason?: string;
  /** Optional quick-log callback for leads */
  onQuickLog?: () => void;
  /** Optional assign crew callback for jobs */
  onAssignCrew?: () => void;
  /** Optional assign truck callback for jobs */
  onAssignTruck?: () => void;
  /** Optional assign estimator callback for quotes */
  onAssignEstimator?: () => void;
  /** Disabled state */
  disabled?: boolean;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function extractCity(address?: string): string {
  if (!address) return '';
  const parts = address.split(',').map(s => s.trim());
  return parts.length >= 2 ? parts[parts.length - 2] : parts[0];
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function getDisplayName(data: Quote | Record<string, unknown>): string {
  const q = data as Quote;
  if (q.type === 'residential') {
    return q.pointOfContact?.name || q.customerCompanyName || 'Unnamed';
  }
  return q.customerCompanyName || q.pointOfContact?.name || 'Unnamed';
}

function isCommercial(data: Quote | Record<string, unknown>): boolean {
  return (data as Quote).type !== 'residential';
}

function getQuoteNumber(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).quoteNumber;
}

function getMoveDate(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).movingDate?.preferredDate;
}

function getEstimateDateRange(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).estimatedMoveDateRange;
}

function getEstimateVisitDate(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).estimateVisitDate;
}

function getEstimatedCost(data: Quote | Record<string, unknown>): number | undefined {
  const q = data as Quote;
  return q.formula?.finalCostEstimate ?? q.formula?.flatRateTotal;
}

function getEstimateLeadName(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).quoteLeadDisplayName;
}

function getFollowUpDate(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).nextFollowUp;
}

function getEstimatedValue(data: Quote | Record<string, unknown>): number | undefined {
  return (data as Quote).estimatedValue;
}

function getCrewInfo(data: Quote | Record<string, unknown>): { assigned: number; needed: number } | undefined {
  const d = data as unknown as Record<string, unknown>;
  const needed = d.logisticsSummary
    ? (d.logisticsSummary as Record<string, number>)?.crewSize
    : (data as Quote).formula?.finalCrewSize;
  if (!needed) return undefined;
  return { assigned: (d.assignedCrew as unknown[] | undefined)?.length ?? 0, needed };
}

function getTruckInfo(data: Quote | Record<string, unknown>): { assigned: number; needed: number } | undefined {
  const d = data as unknown as Record<string, unknown>;
  const needed = d.logisticsSummary
    ? (d.logisticsSummary as Record<string, number>)?.truckQuantity
    : (data as Quote).formula?.truckQuantity;
  if (!needed) return undefined;
  return { assigned: (d.assignedTrucks as unknown[] | undefined)?.length ?? 0, needed };
}

function getOriginAddress(data: Quote | Record<string, unknown>): string | undefined {
  return (data as Quote).addresses?.origin?.address;
}

// ── Lifecycle badge ─────────────────────────────────────────────────────────

const STAGE_CONFIG: Record<LifecycleStage, { label: string; classes: string }> = {
  lead: { label: 'Lead', classes: 'bg-blue-50 text-blue-700' },
  quote: { label: 'Quote', classes: 'bg-plum/10 text-plum' },
  job: { label: 'Job', classes: 'bg-amber-50 text-amber-700' },
};

// ── Type icon ───────────────────────────────────────────────────────────────

function TypeIcon({ commercial, size = 16 }: { commercial: boolean; size?: number }) {
  return commercial
    ? <Building2 size={size} className="text-plum" />
    : <Home size={size} className="text-emerald-600" />;
}


// ── Follow-up date helpers ──────────────────────────────────────────────────

function isFollowUpOverdue(date: string): boolean {
  return new Date(date) < new Date(new Date().toISOString().slice(0, 10));
}

// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════

function DesignC({ data, stage, statusLabel, statusClasses, reason, onClick, onQuickLog, onAssignCrew, onAssignTruck, onAssignEstimator, disabled }: MoveCardProps) {
  const navigate = useNavigate();
  const commercial = isCommercial(data);
  const name = getDisplayName(data);
  const quoteNum = getQuoteNumber(data);
  const moveDate = getMoveDate(data);
  const stageCfg = STAGE_CONFIG[stage];
  const crew = stage === 'job' ? getCrewInfo(data) : undefined;
  const trucks = stage === 'job' ? getTruckInfo(data) : undefined;
  const needsCrew = crew && crew.assigned < crew.needed;
  const needsTrucks = trucks && trucks.assigned < trucks.needed;
  const fullyAssigned = crew && trucks && crew.assigned >= crew.needed && trucks.assigned >= trucks.needed;

  return (
    <div className={`bg-white border border-stone rounded-xl shadow-brand-sm transition-all duration-150
                    ${disabled ? 'opacity-60' : 'hover:border-plum/30 hover:shadow-brand-md'}`}>
      <button
        type="button"
        onClick={disabled ? undefined : (onClick ?? (() => navigate(`/moves/${data.id}`)))}
        disabled={disabled}
        className="w-full text-left px-4 py-3 flex items-start gap-3"
      >
        {/* Left color bar */}
        <div className={`shrink-0 w-1 self-stretch rounded-full ${
          stage === 'lead' ? 'bg-blue-400' : stage === 'quote' ? 'bg-plum' : 'bg-amber-500'
        }`} />

        <div className="flex-1 min-w-0">
          {/* Row 1: Name + quote number + stage/status badges (top-right) */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 flex items-center gap-1.5">
              <TypeIcon commercial={commercial} size={14} />
              <p className="font-semibold text-small text-midnight truncate">{name}</p>
              {quoteNum && (
                <span className="shrink-0 text-[10px] font-mono text-slate">{quoteNum}</span>
              )}
            </div>
            <div className="shrink-0 flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${stageCfg.classes}`}>
                {stageCfg.label}
              </span>
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${statusClasses}`}>
                {statusLabel}
              </span>
            </div>
          </div>

          {/* Row 2: Move date + metadata badges */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            {moveDate && (
              <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-plum/5 text-[9px] text-plum font-medium">
                <Truck size={9} /> {formatMoveDate(moveDate)}
              </span>
            )}
            {!moveDate && getEstimateDateRange(data) && (
              <span className="px-1.5 py-0.5 rounded bg-stone/30 text-[9px] text-slate truncate max-w-[100px]">
                {getEstimateDateRange(data)}
              </span>
            )}
            {getOriginAddress(data) && (
              <span className="flex items-center gap-0.5 text-[10px] text-slate truncate">
                <MapPin size={9} />{extractCity(getOriginAddress(data))}
              </span>
            )}
          </div>

          {/* Row 3: Stage-specific content */}
          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate">
            {stage === 'lead' && (
              <>
                {getFollowUpDate(data) && (
                  <span className={isFollowUpOverdue(getFollowUpDate(data)!) ? 'text-rose font-medium' : ''}>
                    Follow-up {formatMoveDate(getFollowUpDate(data)!)}
                  </span>
                )}
                {getEstimatedValue(data) != null && (
                  <span className="text-emerald-700 font-semibold ml-auto">{formatCurrency(getEstimatedValue(data)!)}</span>
                )}
              </>
            )}
            {stage === 'quote' && (
              <>
                {getEstimateVisitDate(data) && <span>Est {formatMoveDate(getEstimateVisitDate(data)!)}</span>}
                {getEstimateLeadName(data) ? (
                  <span className="truncate">· {getEstimateLeadName(data)}</span>
                ) : onAssignEstimator ? null : (
                  <span className="text-slate/50 italic">No estimator</span>
                )}
                {getEstimatedCost(data) != null && (
                  <span className="text-midnight font-semibold ml-auto">{formatCurrency(getEstimatedCost(data)!)}</span>
                )}
              </>
            )}
            {stage === 'job' && !fullyAssigned && (
              <>
                {crew && <span className={crew.assigned >= crew.needed ? 'text-emerald-600' : ''}>{crew.assigned}/{crew.needed} crew</span>}
                {trucks && <span className={trucks.assigned >= trucks.needed ? 'text-emerald-600' : ''}>{trucks.assigned}/{trucks.needed} trucks</span>}
              </>
            )}
            {stage === 'job' && fullyAssigned && getOriginAddress(data) && !moveDate && (
              <span className="text-emerald-600 font-medium">Fully assigned</span>
            )}
          </div>

          {reason && <p className="text-[10px] text-slate/60 mt-1 truncate">{reason}</p>}
        </div>
      </button>

      {/* Quick actions */}
      {stage === 'lead' && onQuickLog && (
        <div className="border-t border-stone/50 px-4 py-1.5 flex justify-end">
          <button type="button" onClick={onQuickLog}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-plum/30
                       text-[11px] font-medium text-plum hover:bg-plum/5 transition-colors">
            <ClipboardPen size={12} /> Log Communication
          </button>
        </div>
      )}
      {stage === 'quote' && !getEstimateLeadName(data) && onAssignEstimator && (
        <div className="border-t border-stone/50 px-4 py-1.5 flex justify-end">
          <button type="button" onClick={onAssignEstimator}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-plum/30
                       text-[11px] font-medium text-plum hover:bg-plum/5 transition-colors">
            <UserPlus size={12} /> Assign Estimator
          </button>
        </div>
      )}
      {stage === 'job' && (needsCrew || needsTrucks) && (
        <div className="border-t border-stone/50 px-4 py-1.5 flex gap-2">
          {needsCrew && onAssignCrew && (
            <button type="button" onClick={onAssignCrew}
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-dashed
                         border-plum/30 text-[11px] font-medium text-plum hover:bg-plum/5 transition-colors">
              <Users size={12} /> Crew {crew!.assigned}/{crew!.needed}
            </button>
          )}
          {needsTrucks && onAssignTruck && (
            <button type="button" onClick={onAssignTruck}
              className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-dashed
                         border-blue-300 text-[11px] font-medium text-blue-600 hover:bg-blue-50 transition-colors">
              <Truck size={12} /> Truck{(trucks!.needed) > 1 ? 's' : ''} {trucks!.assigned}/{trucks!.needed}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ── Main Export ────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════

export function MoveCard(props: MoveCardProps) {
  return <DesignC {...props} />;
}
